import * as angular from 'angular';

import { IRequestService } from '../request.service';
import { ICurrentApplicationService } from './index';
import { AcquistionTaskConfiguration, CleaningTaskConfiguration, TaskResult, WorkflowTaskConfiguration, GroupNameDto, IDateRange, ITaskConfiguration, OfflineTaskType } from '../../models/index';
import { ILoggerService } from '../../services/general';

export interface ExcludedDates {
    key: GroupNameDto;
    value: string[];
}

export interface ITaskService {
    getAll(force: boolean): angular.IPromise<ITaskConfiguration[]>;
    get(taskGuid: string): angular.IPromise<TaskResult>;

    checkTask(configuration: ITaskConfiguration): angular.IPromise<(ExcludedDates | IDateRange)[]>;
    createTask(configuration: ITaskConfiguration): angular.IPromise<void | IDateRange[]>;

    checkAcquisitionTask(configuration: AcquistionTaskConfiguration): angular.IPromise<IDateRange[]>;
    createAcquisitionTask(taskDefinition: AcquistionTaskConfiguration, force: boolean): angular.IPromise<IDateRange[]>;

    createCleaningTask(taskDefinition: CleaningTaskConfiguration): angular.IPromise<any>;

    createWorkflowTask(configuration: WorkflowTaskConfiguration): angular.IPromise<number>;
    createWorkflowTasks(configurations: WorkflowTaskConfiguration[]): angular.IPromise<any>;
    checkWorkflowTask(configuration): angular.IPromise<ExcludedDates[]>;

    deleteCompletedtasks(): angular.IPromise<any>;
    refresh(): angular.IPromise<void>;
    start(guid: string): angular.IPromise<any>;
    resume(guid: string): angular.IPromise<any>;
    cancel(guid: string): angular.IPromise<any>;
    cancelAll(): angular.IPromise<any>;
}

export class TaskService implements ITaskService {
    static serviceId = 'taskService';

    static $inject = ['$q', 'requestService', 'currentApplicationService', 'loggerService'];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _currentApplicationService: ICurrentApplicationService,
        private _loggerService: ILoggerService
    ) {
    }

    checkTask(configuration: ITaskConfiguration): angular.IPromise<(ExcludedDates | IDateRange)[]> {
        let result = this._$q.when([]);

        switch (configuration.type) {
            case OfflineTaskType.Acquisition:
                result = this.checkAcquisitionTask(configuration as AcquistionTaskConfiguration);
                break;
            case OfflineTaskType.Reprocessing:
                result = this.checkWorkflowTask(configuration as WorkflowTaskConfiguration);
                break;
        }

        return result;
    }

    createTask(configuration: ITaskConfiguration): angular.IPromise<void | IDateRange[]> {
        let result: angular.IPromise<void | IDateRange[]> = this._$q.reject('Failed to create task.');

        switch (configuration.type) {
            case OfflineTaskType.Acquisition:
                result = this.createAcquisitionTask(configuration as AcquistionTaskConfiguration, true);
                break;
            case OfflineTaskType.Cleaning:
                result = this.createCleaningTask(configuration as CleaningTaskConfiguration);
                break;
            case OfflineTaskType.Reprocessing:
                result = this.createWorkflowTask(configuration as WorkflowTaskConfiguration)
                    .then(data => {
                        if (data < 0) {
                            switch (data) {
                                case -2:
                                    return this._$q.reject('Tasks cannot be executed on a child application.');
                                default:
                                    return this._$q.reject('Failed to create task.');
                            }
                        }
                        // otherwise ok
                        return;
                    });
                break;
            default:
                this._loggerService.logError('Unsupported task type.');
                break;
        }

        return result;
    }

    checkAcquisitionTask(configuration: AcquistionTaskConfiguration): angular.IPromise<IDateRange[]> {
        return this._requestService.postAtV1(`task/CheckAcquisitionTask`, configuration);
    }

    createAcquisitionTask(taskDefinition: AcquistionTaskConfiguration, force: boolean): angular.IPromise<IDateRange[]> {
        const defered = this._$q.defer<IDateRange[]>();
        if (!taskDefinition.databaseGuid) {
            const databaseGuid = this._currentApplicationService.getApplicationConfiguration().defaultDatabaseGuid;
            taskDefinition.databaseGuid = databaseGuid;
        }
        const url = `task/CreateAcquisitionTask${force ? '/true' : ''}`;
        this._requestService
            .postAtV1<IDateRange[]>(url, taskDefinition)
            .then(data => {
                this.cleanCurrentcache();
                defered.resolve(data);
            })
            .catch(reason => defered.reject(reason));
        return defered.promise;
    }

    createCleaningTask(taskDefinition: CleaningTaskConfiguration): angular.IPromise<any> {
        let promise: angular.IPromise<any>;
        const action: string = 'CreateCleaningTask';
        try {
            const url = `task/${action}`;
            promise = this._requestService.postAtV1(url, taskDefinition);
        } catch (e) {
            this._loggerService.logError(e);
            promise = this._$q.reject(e);
        }
        return promise;
    }

    getAll(force?: boolean): angular.IPromise<ITaskConfiguration[]> {
        if (!!force) {
            this._requestService.cleanCacheAtV1('task');
        }
        return this._requestService.getAtV1('task');
    }

    get(taskGuid: string): angular.IPromise<TaskResult> {
        this._requestService.cleanCacheAtV1(`task?taskGuid=${taskGuid}`);
        return this._requestService.getAtV1('task', { taskGuid: taskGuid });
    }

    deleteCompletedtasks(): angular.IPromise<any> {
        const defered = this._$q.defer();
        this._requestService.deleteAtV1('task/deleteCompleted')
            .then(data => {
                this.cleanCurrentcache();
                defered.resolve(data);
            })
            .catch((reason) => defered.reject(reason));
        return defered.promise;
    }

    refresh(): angular.IPromise<any> {
        const defered = this._$q.defer();
        this.cleanCurrentcache();
        defered.resolve();
        return defered.promise;
    }

    cancel(guid: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        this._requestService.postAtV1(`task/cancel/${guid}`)
            .then(result => {
                this.cleanCurrentcache();
                defered.resolve();
            })
            .catch(() => {
                defered.reject();
            });
        return defered.promise;
    }

    cancelAll(): angular.IPromise<any> {
        return this._requestService.postAtV1('task/cancel/all')
            .then(data => {
                this.cleanCurrentcache();
                return data;
            });
    }

    start(guid: string): angular.IPromise<any> {
        return this.startOrResume(`task/start/${guid}`);
    }

    resume(guid: string): angular.IPromise<any> {
        return this.startOrResume(`task/resume/${guid}`);
    }

    checkWorkflowTask(configuration): angular.IPromise<ExcludedDates[]> {
        return this._requestService.postAtV1(`task/checkWorkflowTask`, configuration);
    }

    createWorkflowTask(configuration: WorkflowTaskConfiguration): angular.IPromise<number> {
        return this._requestService.postAtV1<number>(`task/createWorkflowTask`, configuration)
            .then(data => {
                this.cleanCurrentcache();
                this._requestService.cleanCacheAtV1(`group/configuration/${configuration.groupGuid}/`);
                return data;
            });
    }

    createWorkflowTasks(configurations: WorkflowTaskConfiguration[]): angular.IPromise<any> {
        return this._requestService.postAtV1(`task/createWorkflowTasks`, configurations);
    }

    private controllerUrl(): string {
        return this._requestService.getUrlV1() + 'task';
    }

    private cleanCurrentcache() {
        this._requestService.cleanCache(this.controllerUrl());
    }

    private startOrResume(url: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        this._requestService
            .postAtV1(url)
            .then(result => {
                this.cleanCurrentcache();
                defered.resolve();
            })
            .catch(() => {
                defered.reject();
            });
        return defered.promise;
    }
}